import { useState } from "react";
import { Link } from "react-router-dom";
import { Download, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { useApi } from "../../lib/useApi";
import { api, formatApiError } from "../../lib/api";
import { Loader, ErrorState, PageHeader } from "../../components/common/States";
import CommunicationConsents from "../../components/comms/CommunicationConsents";
import { ConfirmButton } from "../../components/common/ConfirmButton";
import { fmtDate } from "../../lib/labels";

const PREFS = [
  ["show_name_public", "Afficher mon prénom sur mes avis publics", "Sinon vos avis publiés apparaissent sous la mention « Usager ». Appliqué à vos prochains dépôts."],
  ["share_contact", "Transmettre mon email et téléphone aux organisations", "Permet aux organisations de vous recontacter directement. Désactivé : seul votre nom leur est transmis, les échanges se font dans votre espace."],
  ["email_notifications", "Recevoir un email à chaque nouveau message", "L'email vous prévient sans reprendre le contenu du message, consultable uniquement dans votre espace."],
];

const Preferences = ({ initial }) => {
  const [prefs, setPrefs] = useState(initial);
  const toggle = async (k, v) => {
    const next = { ...prefs, [k]: v };
    setPrefs(next);
    try { await api.put("/me/preferences", next); toast.success("Préférence enregistrée"); }
    catch (e) { setPrefs(prefs); toast.error(formatApiError(e)); }
  };
  return (
    <div className="card-surface divide-y">
      {PREFS.map(([k, t, d]) => (
        <label key={k} className="flex items-center justify-between gap-6 p-5">
          <span><span className="block font-medium">{t}</span><span className="mt-0.5 block text-sm text-slate-500">{d}</span></span>
          <Switch checked={!!prefs[k]} onCheckedChange={(v) => toggle(k, v)} data-testid={`pref-${k}`} />
        </label>
      ))}
    </div>
  );
};

const ExportCard = () => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/me/export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "mes-donnees-voxa-afrika.json"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card-surface flex flex-wrap items-center justify-between gap-4 p-6" data-testid="export-card">
      <div><p className="font-display font-semibold">Exporter mes données</p><p className="text-sm text-slate-500">Profil, feedbacks, échanges, notifications, tickets de support et consentements, au format JSON.</p></div>
      <Button variant="outline" onClick={download} disabled={busy} data-testid="export-data-button">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}Télécharger</Button>
    </div>
  );
};

const STATUS_FR = { pending: "en cours de traitement", rejected: "refusée", cancelled: "annulée", approved: "acceptée" };

const Deletion = () => {
  const { data, loading, reload } = useApi("/me/deletion-request");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const req = data?.request;
  const send = async () => {
    setBusy(true);
    try { await api.post("/me/deletion-request", { reason }); toast.success("Demande envoyée"); reload(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const cancel = async () => {
    try { await api.delete("/me/deletion-request"); toast.success("Demande annulée"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (loading) return <Loader />;
  return (
    <div className="card-surface space-y-4 border-rose-200 p-6" data-testid="deletion-card">
      <p className="font-display font-semibold text-rose-700">Supprimer mon compte</p>
      <p className="text-sm text-slate-600">Après validation par notre équipe, vos informations personnelles sont effacées et vos feedbacks sont anonymisés. Cette action est irréversible.</p>
      {req && <p className="text-sm" data-testid="deletion-status">Dernière demande du {fmtDate(req.created_at)} : <b>{STATUS_FR[req.status]}</b></p>}
      {req?.status === "pending" ? (
        <Button variant="outline" onClick={cancel} data-testid="deletion-cancel-button">Annuler ma demande</Button>
      ) : (
        <>
          <Textarea rows={3} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Raison (facultatif)" data-testid="deletion-reason-input" />
          <ConfirmButton testId="deletion-submit" title="Demander la suppression de votre compte ?" description="Notre équipe traitera votre demande. Vos données seront définitivement effacées."
            confirmLabel="Envoyer la demande" onConfirm={send}
            trigger={<Button variant="destructive" disabled={busy} data-testid="deletion-submit">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}Demander la suppression</Button>} />
        </>
      )}
    </div>
  );
};

export default function UserPrivacy() {
  const { data, loading, error, reload } = useApi("/me/profile");
  if (loading) return <Loader />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div className="max-w-3xl space-y-6">
      <PageHeader title="Confidentialité" description={<>Vos coordonnées ne sont jamais publiées. <Link to="/confidentialite" className="text-blue-600" data-testid="privacy-policy-link">Lire la politique de confidentialité</Link></>} />
      <Preferences initial={data.preferences} />
      <CommunicationConsents />
      <ExportCard />
      <Deletion />
    </div>
  );
}
